/**
 * AddFieldController — AK-334 «Tap-to-field» императивный слой.
 *
 * Управляет визуалом режима «+ Поле» на MapLibre:
 *   - курсор-прицел (crosshair) на канве, пока режим активен;
 *   - слушатель тапа: тап по СУЩЕСТВУЮЩЕМУ полю (слой field-click-target) НЕ
 *     запускает детект — отдаём наверх (callback), карта открывает карточку поля
 *     штатно; тап по свободному месту → onDetectPoint(lng,lat);
 *   - пульс-лоадер на точке тапа (HTML-маркер) во время детекта;
 *   - amber-ПУНКТИРНЫЙ черновик-контур (fill + dashed line) поверх карты.
 *
 * Изоляция (ADR-038, жёстко): свои source/layers в namespace `af-` — НЕ трогаем
 * fields-mvt/registry/LayerManager/measure/editor. Цвет amber резолвим в рантайме
 * (resolveAmberHex) — MapLibre paint не ест hsl(var(--token)).
 *
 * КОНТРОЛЛЕР НЕ держит машину состояний — её держит addFieldStore; сюда стекают
 * только императивные команды (activate/showDetecting/showDraft/clearOverlay)
 * через applyAddFieldState.
 */
import maplibregl from 'maplibre-gl';
import { resolveAmberHex } from '../editor/earthtech';
import { isMapAlive } from '../isMapAlive';
import type { DelineateGeometry } from './delineateApi';
import type { Point } from './addFieldStore';

const SRC_DRAFT = 'af-draft-src';
const LAYER_DRAFT_FILL = 'af-draft-fill';
const LAYER_DRAFT_LINE = 'af-draft-line';
/** Слой-цель для распознавания «тап по существующему полю». */
const FIELD_CLICK_TARGET = 'field-click-target';

export interface AddFieldCallbacks {
  /** Тап по свободному месту — запросить детект по точке [lng, lat]. */
  onDetectPoint: (point: Point) => void;
  /**
   * Тап по существующему полю системы — детект НЕ зовём. Возвращаем id, чтобы
   * v2.tsx открыл штатную карточку поля (обычное поведение карты).
   */
  onExistingField: (fieldId: string, lngLat: maplibregl.LngLat) => void;
}

export class AddFieldController {
  private amber = resolveAmberHex();
  private installed = false;
  /** HTML-маркер пульса на точке тапа (во время детекта). */
  private pulseMarker: maplibregl.Marker | null = null;

  constructor(
    private map: maplibregl.Map,
    private cb: AddFieldCallbacks,
  ) {}

  // ── lifecycle ──────────────────────────────────────────────────────────────

  activate(): void {
    if (this.installed) return;
    this.amber = resolveAmberHex();
    this.ensureDraftLayers();
    this.map.getCanvas().style.cursor = 'crosshair';
    this.map.on('click', this.onClick);
    this.installed = true;
  }

  deactivate(): void {
    if (!this.installed) return;
    this.map.off('click', this.onClick);
    this.map.getCanvas().style.cursor = '';
    this.clearOverlay();
    this.installed = false;
  }

  /** Полный демонтаж (удаление слоёв/source) — при destroy карты. */
  destroy(): void {
    this.deactivate();
    this.removePulse();
    // P0 (AK-343): destroy() runs during MapPageV2 unmount. If another effect
    // cleanup already called `map.remove()` (order not guaranteed under fast
    // navigation like /map → /team), getLayer/getSource throw on the nulled
    // style → error boundary. remove() already freed our af-* layers/source.
    if (!isMapAlive(this.map)) return;
    for (const id of [LAYER_DRAFT_LINE, LAYER_DRAFT_FILL]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SRC_DRAFT)) this.map.removeSource(SRC_DRAFT);
  }

  // ── команды из reducer ──────────────────────────────────────────────────────

  /** Пульс-лоадер на точке тапа (карта НЕ блокируется). */
  showDetecting(point: Point): void {
    this.clearDraftGeometry();
    this.showPulse(point);
  }

  /** Нарисовать amber-пунктирный черновик. */
  showDraft(geometry: DelineateGeometry): void {
    this.removePulse();
    this.ensureDraftLayers();
    const src = this.map.getSource(SRC_DRAFT) as maplibregl.GeoJSONSource | undefined;
    src?.setData({
      type: 'Feature',
      properties: {},
      geometry,
    } as GeoJSON.Feature);
  }

  /** Убрать оверлей: пульс + черновик. */
  clearOverlay(): void {
    this.removePulse();
    this.clearDraftGeometry();
  }

  // ── internals ───────────────────────────────────────────────────────────────

  private onClick = (e: maplibregl.MapMouseEvent): void => {
    // Существует ли под курсором поле системы? Если да — детект НЕ зовём.
    let hitFieldId: string | null = null;
    try {
      const feats = this.map.queryRenderedFeatures(e.point, {
        layers: this.map.getLayer(FIELD_CLICK_TARGET) ? [FIELD_CLICK_TARGET] : [],
      });
      const id = (feats[0]?.properties as { id?: string } | undefined)?.id;
      if (id) hitFieldId = id;
    } catch {
      // Слой ещё не готов — трактуем как свободное место.
    }
    if (hitFieldId) {
      this.cb.onExistingField(hitFieldId, e.lngLat);
      return;
    }
    this.cb.onDetectPoint([e.lngLat.lng, e.lngLat.lat]);
  };

  private ensureDraftLayers(): void {
    if (!this.map.getSource(SRC_DRAFT)) {
      this.map.addSource(SRC_DRAFT, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!this.map.getLayer(LAYER_DRAFT_FILL)) {
      this.map.addLayer({
        id: LAYER_DRAFT_FILL,
        type: 'fill',
        source: SRC_DRAFT,
        paint: { 'fill-color': this.amber, 'fill-opacity': 0.16 },
      });
    }
    if (!this.map.getLayer(LAYER_DRAFT_LINE)) {
      this.map.addLayer({
        id: LAYER_DRAFT_LINE,
        type: 'line',
        source: SRC_DRAFT,
        paint: {
          'line-color': this.amber,
          'line-width': 3,
          // Пунктир = «черновик, не сохранено».
          'line-dasharray': [3, 2],
        },
      });
    }
  }

  private clearDraftGeometry(): void {
    // P0 (AK-343): reachable from deactivate()→clearOverlay() during unmount
    // teardown, which may run after `map.remove()`. getSource() throws on a
    // nulled style — bail.
    if (!isMapAlive(this.map)) return;
    const src = this.map.getSource(SRC_DRAFT) as maplibregl.GeoJSONSource | undefined;
    src?.setData({ type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection);
  }

  private showPulse(point: Point): void {
    this.removePulse();
    const el = document.createElement('div');
    el.className = 'af-pulse';
    el.setAttribute('data-testid', 'add-field-pulse');
    // Инлайн-стиль пульса: amber-точка с расходящейся волной. Цвет — резолвленный
    // amber (не токен) т.к. это императивный DOM вне Tailwind-графа.
    el.style.setProperty('--af-amber', this.amber);
    this.pulseMarker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(point)
      .addTo(this.map);
  }

  private removePulse(): void {
    this.pulseMarker?.remove();
    this.pulseMarker = null;
  }
}
